<template>
  <div>
    <!-- Title -->
    <Title />

    <!-- Main Card -->
    <v-card class="mx-auto mt-5" tile>
      <v-list shaped>

        <!-- Farm Selection -->
        <v-list-item>
          <v-row>
            <v-col cols="12" md="10">
              <h4>Farm:</h4>
              <v-select v-model="currentFarmId" :items="farmList" item-value="id" item-title="name" label="Select Farm"
                @change="changeFarm" solo dense class="selection">
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <img :src="item.avatar" class="avatar mr-2" />
                    <span>{{ item.name }} - {{ item.active_pond_count }} Active</span>
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <img :src="item.avatar" class="avatar mr-2" />
                    <span>{{ item.name }} - {{ item.active_pond_count }} Active</span>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-list-item>

        <!-- Loading Spinner -->
        <v-container fluid class="d-flex justify-center align-center">
          <v-progress-circular v-if="loadingStatus" indeterminate color="primary" size="32"></v-progress-circular>
        </v-container>

        <!-- Pond Selection -->
        <v-list-item v-if="currentFarmId && !loadingStatus">
          <v-row>
            <v-col cols="12" md="10">
              <h4>Pond:</h4>
              <v-select v-model="cycleId" :items="pondList" item-value="id" item-title="name" label="Select Pond"
                @change="changePond" solo dense class="pond-select" />
            </v-col>
          </v-row>
        </v-list-item>

        <!-- Partial Harvest Table -->
        <v-list-item v-if="cycleId">
          <v-row>
            <v-col cols="12" md="10">
              <h4>Existing Partial Harvest</h4>
              <v-simple-table class="mt-0">
                <thead>
                  <tr>
                    <th class="text-center custom-header">Partial Havest Date</th>
                    <th class="text-center custom-header">Biomass Taken Out (lbs)</th>
                    <th class="text-center custom-header"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredPartialHarvest" :key="item.id || index">
                    <td class="text-center">{{ formatDate(item.partial_harvest_date) }}</td>
                    <td class="text-center">{{ item.partial_harvest_biomass }}</td>
                    <td class="text-center">
                      <v-btn v-if="item.partial_harvest_biomass" small color="red" class="white--text"
                        @click="confirmDeletePartialHarvest(index)">
                        Delete
                      </v-btn>
                    </td>
                  </tr>

                  <!-- Row for Adding New -->
                  <tr v-if="addingNew">
                    <td>
                      <v-menu v-model="datePickerMenu" :close-on-content-click="false" transition="scale-transition"
                        offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="newPartialHarvestDate" label="Select Date" readonly dense hide-details
                            solo v-bind="attrs" v-on="on" />
                        </template>
                        <v-date-picker v-model="newPartialHarvestDate" @input="datePickerMenu = false" :max="today" />
                      </v-menu>
                    </td>
                    <td class="text-center">
                      <v-text-field v-model="newPartialHarvestBiomass" type="text" label="Input Partial Harvest Biomass"
                        hide-details="auto" dense solo :error-messages="errorMessages"
                        @input="validatePartialHarvest" />
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Button Controls -->
              <v-btn small color="primary" class="mt-2" @click="toggleaddpartialharvest">
                {{ addpartialharvestButtonLabel }}
              </v-btn>
              <v-btn v-if="addingNew" small color="primary" class="mt-2 ml-2" @click="savePartialHarvest">
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-list-item>

      </v-list>
    </v-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Title from '@/components/Title.vue';
import tokenAxios from "@/components/tokenAxios";
import moment from "moment";
import Swal from "sweetalert2";

export default {
  components: { Title },
  computed: {
    ...mapState(["isMobile", "user_info", "isMinnowtech"]),
    filteredPartialHarvest() {
      return this.partialHarvestList
        .filter(item => item.partial_harvest_biomass !== null)
        .sort((a, b) => new Date(a.partial_harvest_date) - new Date(b.partial_harvest_date));
    },
    filteredActivePonds() {
      return this.pondList.filter(item => item.completed === null);
    },
    addpartialharvestButtonLabel() {
      return this.addingNew ? 'Cancel' : 'Add New Partial Harvest';
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    },
  },
  created() {
    this.fetchFarms();
  },
  data() {
    return {
      API_SERVER: process.env.VUE_APP_API_SERVER,
      currentFarmId: null,
      farmList: [],
      pondList: [],
      partialHarvestList: [],
      addingNew: false,
      newPartialHarvestBiomass: null,
      errorMessages: [],
      newPartialHarvestDate: null,
      cycleId: null,
      stockingDate: null,
      loadingStatus: false,
      datePickerMenu: false,
    };
  },
  methods: {
    // Validation
    validatePartialHarvest() {
      this.errorMessages = [];
      if (!this.newPartialHarvestBiomass) return;
      if (this.newPartialHarvestBiomass >= 5000) {
        this.errorMessages.push('Parital Harvest must be less than 5000 lbs');
      }
    },

    // Fetch Farms
    async fetchFarms(page = 1) {
      await this.withLoading(async () => {
        const response = await tokenAxios(this.user_info.access_token).get(`${this.API_SERVER}/farm/farm/?format=json&page=${page}`);
        if (response.status === 200 && response.data.length) {
          this.farmList = response.data
            .filter(farm => farm.status === 1)
            .map(o => ({
              ...o,
              avatar: o.avatar || "https://farm-avatar.s3.amazonaws.com/minnowtech.jpg",
            }));
        }
      });
    },

    changeFarm(farm) {
      this.resetData();
      this.fetchPonds(farm);
    },

    // Fetch Ponds
    async fetchPonds(farm) {
      await this.withLoading(async () => {
        const response = await tokenAxios(this.user_info.access_token).get(`${this.API_SERVER}/farm/pond/?status=1&no_page=1&format=json&farm=${farm}`);
        if (response.status === 200 && response.data.length) {
          this.pondList = response.data
            .filter(o => o.completed === null)
            .map(o => ({
              ...o,
              text: `Pond ${o.pond_id} : Stocked on ${o.date}`,
            }));
        }
      });
    },

    changePond(cycleId) {
      const selected = this.pondList.find(p => p.id === cycleId);
      if (selected) this.stockingDate = selected.date;
      this.partialHarvestList = [];
      this.fetchPartialHarvest(cycleId);
    },

    // Fetch Partial Harvest
    async fetchPartialHarvest(cycleId) {
      await this.withLoading(async () => {
        const response = await tokenAxios(this.user_info.access_token).get(`${this.API_SERVER}/farm/results/?format=json&pond=${cycleId}`);
        if (response.status === 200 && response.data.results.length) {
          this.partialHarvestList = response.data.results;
        }
      });
    },

    toggleaddpartialharvest() {
      this.resetForm();
      this.addingNew = !this.addingNew;
    },

    async savePartialHarvest() {
      this.validatePartialHarvest();
      if (this.errorMessages.length) {
        Swal.fire("Invalid input!", this.errorMessages.join("\n"), "error");
        return;
      }

      const woc = this.calculateWOC();
      const formData = new FormData();
      formData.append("pond", this.cycleId);
      formData.append("woc", woc);
      formData.append("partial_harvest_biomass", this.newPartialHarvestBiomass);
      formData.append("partial_harvest_date", this.newPartialHarvestDate);

      await this.withLoading(async () => {
        try {
          const response = await tokenAxios(this.user_info.access_token).post(`${this.API_SERVER}/farm/results/savePartialHarvest/`, formData);
          if (response.status === 201) {
            await this.fetchPartialHarvest(this.cycleId);
            Swal.fire("Success!", "Partial harvest added successfully.", "success");
            this.resetForm();
          }
        } catch (error) {
          if (error.response && error.response.status === 400) {
            Swal.fire("Error!", error.response.data.error, "error");
          } else {
            Swal.fire("Error!", "Failed to add partial harvest.", "error");
          }
        }
      });
    },

    confirmDeletePartialHarvest(index) {
      if (confirm("Are you sure you want to delete this partial harvest data?")) {
        this.deletePartialHarvest(index);
      }
    },

    deletePartialHarvest(index) {
      const partialHarvestId = this.partialHarvestList[index].id;
      this.partialHarvestList.splice(index, 1);
      tokenAxios(this.user_info.access_token)
        .delete(`${this.API_SERVER}/farm/results/${partialHarvestId}/`)
        .catch(error => {
          console.error('Error deleting partial harvest data:', error);
        });
    },

    calculateWOC() {
      const doc = moment(this.newPartialHarvestDate).diff(moment(this.stockingDate), 'days');
      return Math.floor((doc - 1) / 7) + 1;
    },

    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    resetData() {
      this.partialHarvestList = [];
      this.pondList = [];
      this.cycleId = null;
    },

    resetForm() {
      this.newPartialHarvestBiomass = null;
      this.newPartialHarvestDate = null;
      this.errorMessages = [];
    },

    async withLoading(callback) {
      this.loadingStatus = true;
      try {
        await callback();
      } finally {
        this.loadingStatus = false;
      }
    },
  },
};
</script>

<style scoped>
.avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.selection .v-input__slot {
  background-color: #edfeee !important;
}
</style>
